interface Config {
  api: {
    baseUrl: string;
  };
  app: {
    name: string;
    version: string;
    environment: string;
  };
  features: {
    debugMode: boolean;
    analytics: boolean;
  };
  ui: {
    defaultTheme: 'light' | 'dark';
    itemsPerPage: number;
  };
  okta: {
    issuer: string;
    clientId: string;
    redirectUri: string;
    postLogoutRedirectUri: string;
    scopes: string[];
    enabled: boolean;
  };
}

const config: Config = {
  api: {
    baseUrl: import.meta.env.VITE_API_BASE_URL || '/api',
  },
  app: {
    name: import.meta.env.VITE_APP_NAME || 'Initiative Tracker',
    version: import.meta.env.VITE_APP_VERSION || '1.0.0',
    environment: import.meta.env.VITE_ENVIRONMENT || 'development',
  },
  features: {
    debugMode: import.meta.env.VITE_ENABLE_DEBUG_MODE === 'true' ||
      import.meta.env.VITE_DEBUG === 'true' ||
      import.meta.env.VITE_VERBOSE_LOGS === 'true',
    analytics: import.meta.env.VITE_ENABLE_ANALYTICS === 'true',
  },
  ui: {
    defaultTheme: (import.meta.env.VITE_DEFAULT_THEME as 'light' | 'dark') || 'light',
    itemsPerPage: parseInt(import.meta.env.VITE_ITEMS_PER_PAGE || '25'),
  },
  okta: {
    issuer: import.meta.env.VITE_OKTA_ISSUER || 'https://your-okta-domain.okta.com/oauth2/default',
    clientId: import.meta.env.VITE_OKTA_CLIENT_ID || '',
    redirectUri: import.meta.env.VITE_OKTA_REDIRECT_URI || `${window.location.origin}/login/callback`,
    postLogoutRedirectUri: import.meta.env.VITE_OKTA_POST_LOGOUT_REDIRECT_URI || window.location.origin,
    scopes: ['openid', 'profile', 'email'],
    enabled: import.meta.env.VITE_OKTA_AUTH_ENABLED !== 'false'
  },
};

// Validation function
const validateConfig = (): boolean => {
  console.log('🔍 Validating frontend configuration...');

  const requiredFields = ['api.baseUrl', 'app.name'];
  const missingFields: string[] = [];
  const warnings: string[] = [];

  // Check required fields
  requiredFields.forEach(field => {
    const keys = field.split('.');
    let value: any = config;

    for (const key of keys) {
      value = value[key];
      if (value === undefined || value === null || value === '') {
        missingFields.push(field);
        break;
      }
    }
  });

  if (missingFields.length > 0) {
    console.error('❌ Missing required environment variables:', missingFields.join(', '));
    return false;
  }

  // Validate API URL format (allow relative URLs for proxy setup)
  if (config.api.baseUrl && !config.api.baseUrl.startsWith('http') && !config.api.baseUrl.startsWith('/')) {
    console.error('❌ VITE_API_BASE_URL must start with http://, https://, or / (for proxy)');
    return false;
  }

  // Check for common misconfigurations
  if (config.api.baseUrl?.includes('localhost:3001')) {
    warnings.push('API base URL points to port 3001 - ensure this matches your backend port');
  }

  // Validate port configuration
  if (config.api.baseUrl?.includes('localhost')) {
    const portMatch = config.api.baseUrl.match(/:(\d+)/);
    if (portMatch) {
      const port = parseInt(portMatch[1]);
      if (port === 3000) {
        warnings.push('API base URL points to frontend port (3000) - this may cause circular requests');
      }
    }
  }

  // Check environment setup
  const isProduction = import.meta.env.PROD;
  const isDevelopment = import.meta.env.DEV;

  if (!isProduction && !isDevelopment) {
    warnings.push('Unable to determine environment mode (PROD/DEV)');
  }

  // Log warnings
  if (warnings.length > 0) {
    warnings.forEach(warning => console.warn(`⚠️ ${warning}`));
  }

  console.log('✅ Frontend configuration validated successfully');
  console.log(`ℹ️ Environment: ${isProduction ? 'Production' : 'Development'}`);
  console.log(`ℹ️ API Base URL: ${config.api.baseUrl || 'default (/api)'}`);

  return true;
};

// Validate configuration
if (config.app.environment !== 'test') {
  if (validateConfig()) {
    console.log('✅ Frontend configuration validated successfully');
  } else {
    console.error('❌ Frontend configuration validation failed');
  }
}

export default config;
